import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  APP_LANGUAGE_LABEL_KEYS,
  APP_LANGUAGE_ORDER,
  DEFAULT_APP_LANGUAGE,
  type AppLanguage,
  persistUserSelectedAppLanguage,
  resolveAppLanguage,
  zIndexMap,
} from '../config';
import { useClickOutside } from '../hooks/use-click-outside';

import { BaseButton } from './base-primitives.styles';
import { rubikTypographyStyles } from './typography.styles';

const LanguageSelectorContainer = styled.div.attrs({
  'data-ui': 'shared-app-language-selector',
})`
  ${rubikTypographyStyles};
  position: relative;
  width: ${({ theme }) => theme.scale.px(129)};
  min-height: ${({ theme }) => theme.scale.px(25)};
  border-radius: ${({ theme }) => theme.scale.px(6)};
  background: ${({ theme }) => theme.colors.surface.subtle};
  z-index: ${zIndexMap.shared.languageSelector};

  & > [data-ui='shared-app-language-selector-menu'] {
    margin-top: ${({ theme }) => theme.scale.px(3)};
  }
`;

const LanguageSelectorButton = styled(BaseButton)`
  width: 100%;
  min-height: ${({ theme }) => theme.scale.px(25)};
  border-radius: inherit;
  padding: 0 ${({ theme }) => theme.scale.px(12)} 0
    ${({ theme }) => theme.scale.px(20)};
  font-size: ${({ theme }) => theme.scale.px(10)};
  font-weight: 600;
  text-align: left;
  white-space: nowrap;
`;

const LanguageSelectorMenu = styled.div.attrs({
  'data-ui': 'shared-app-language-selector-menu',
})`
  position: absolute;
  left: 0;
  top: 100%;
  width: 100%;
  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.scale.px(6)};
  display: grid;
  gap: 0;
  background: ${({ theme }) => theme.colors.surface.subtle};
  z-index: ${zIndexMap.shared.languageMenu};
`;

const LanguageSelectorOption = styled(BaseButton)<{ $isSelected: boolean }>`
  box-sizing: border-box;
  width: 100%;
  min-height: ${({ theme }) => theme.scale.px(25)};
  border-radius: ${({ theme }) => theme.scale.px(6)};
  border: ${({ theme }) => theme.scale.px(2)} solid transparent;
  background: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.brand.secondary : 'transparent'};
  background-clip: padding-box;
  padding: 0 ${({ theme }) => theme.scale.px(13)} 0
    ${({ theme }) => theme.scale.px(14)};
  font-size: ${({ theme }) => theme.scale.px(10)};
  font-weight: 600;
  text-align: left;
  white-space: nowrap;
  cursor: ${({ $isSelected }) => ($isSelected ? 'default' : 'pointer')};
`;

export function AppLanguageSelector() {
  const { i18n, t } = useTranslation();
  const selectedLanguage = resolveAppLanguage(i18n.language);
  const languageOptions = APP_LANGUAGE_ORDER.map(value => ({
    label: t(APP_LANGUAGE_LABEL_KEYS[value]),
    value,
  }));
  const selectedLanguageLabel =
    languageOptions.find(option => option.value === selectedLanguage)?.label ??
    t(APP_LANGUAGE_LABEL_KEYS[DEFAULT_APP_LANGUAGE]);
  const [isLanguageMenuOpen, setLanguageMenuOpen] = useState(false);
  const languageSelectorRef = useRef<HTMLDivElement>(null);

  useClickOutside({
    enabled: isLanguageMenuOpen,
    onOutsideClick: () => setLanguageMenuOpen(false),
    refs: [languageSelectorRef],
  });

  const toggleLanguageMenu = () => {
    setLanguageMenuOpen(value => !value);
  };

  const handleLanguageSelect = (language: AppLanguage) => {
    if (language === selectedLanguage) {
      setLanguageMenuOpen(false);
      return;
    }

    persistUserSelectedAppLanguage(language);
    void i18n.changeLanguage(language);
    setLanguageMenuOpen(false);
  };

  return (
    <LanguageSelectorContainer ref={languageSelectorRef}>
      <LanguageSelectorButton
        aria-label={t('login.languageAria')}
        type="button"
        onClick={toggleLanguageMenu}
      >
        {selectedLanguageLabel}
      </LanguageSelectorButton>

      {isLanguageMenuOpen ? (
        <LanguageSelectorMenu>
          {languageOptions.map(option => (
            <LanguageSelectorOption
              $isSelected={option.value === selectedLanguage}
              key={option.value}
              type="button"
              onClick={() => handleLanguageSelect(option.value)}
            >
              {option.label}
            </LanguageSelectorOption>
          ))}
        </LanguageSelectorMenu>
      ) : null}
    </LanguageSelectorContainer>
  );
}
